import axios from 'axios';

import type {
  GuardrailRuleCreateParams,
  GuardrailRuleInfo,
  GuardrailRuleUpdateParams,
} from '~/server_shared/openapi/data-contracts';
import { reportAxiosError } from '~/Utils';

/**
 * API functions for guardrail rule CRUD. Reads go through `useDataFetcher`;
 * these are the mutating calls. Each wraps the error in `reportAxiosError`
 * (toast) and rethrows so the caller can keep the dialog open and reset the
 * submitting flag.
 */
const baseUrl = (organizationId: number) => `/api/v1/organizations/${organizationId}/guardrails/rules`;

export const createGuardrailRule = async (
  organizationId: number,
  values: GuardrailRuleCreateParams
): Promise<GuardrailRuleInfo> => {
  try {
    const response = await axios.post<GuardrailRuleInfo>(baseUrl(organizationId), values);
    return response.data;
  } catch (error) {
    await reportAxiosError(error);
    throw error;
  }
};

export const updateGuardrailRule = async (
  organizationId: number,
  ruleId: number,
  values: GuardrailRuleUpdateParams
): Promise<GuardrailRuleInfo> => {
  try {
    const response = await axios.put<GuardrailRuleInfo>(`${baseUrl(organizationId)}/${ruleId}`, values);
    return response.data;
  } catch (error) {
    await reportAxiosError(error);
    throw error;
  }
};

export const deleteGuardrailRule = async (organizationId: number, ruleId: number): Promise<void> => {
  try {
    await axios.delete(`${baseUrl(organizationId)}/${ruleId}`);
  } catch (error) {
    await reportAxiosError(error);
    throw error;
  }
};

export const setGuardrailRuleEnabled = async (
  organizationId: number,
  ruleId: number,
  isEnabled: boolean
): Promise<void> => {
  try {
    await axios.patch(`${baseUrl(organizationId)}/${ruleId}/enable`, { is_enabled: isEnabled });
  } catch (error) {
    await reportAxiosError(error);
    throw error;
  }
};
